import {
  Injectable,
  inject,
} from '@angular/core';
import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpStatusCode,
} from '@angular/common/http';
import {
  Observable,
  throwError,
} from 'rxjs';
import {catchError} from 'rxjs/operators';
import {Router} from '@angular/router';
import {OidcAuthService} from '../oidc-auth/oidc-auth.service';
import {IframeService} from '../../iframe/iframe.service';
import {ApiUrlService} from '../../api/core/api-url/api-url.service';
import {Links} from '../../../../common/constants/links';
import {FlowableApiUrlService} from '../../../domain/flowable/api/api-url/flowable-api-url.service';
import {Constants} from '../../../../common/constants/constants';
import {BackgroundTaskService} from '../../page/error/background-task/background-task.service';

@Injectable()
export class ErrorInterceptor implements HttpInterceptor {

  private router = inject(Router);

  private backgroundTaskService = inject(BackgroundTaskService);

  private iframeService = inject(IframeService);

  private oidcAuthService = inject(OidcAuthService);

  private apiUrlService = inject(ApiUrlService);

  private flowableApiUrlService = inject(FlowableApiUrlService);

  private serverErrorCodes: HttpStatusCode[] = [HttpStatusCode.InternalServerError, HttpStatusCode.NotImplemented,
    HttpStatusCode.BadGateway, HttpStatusCode.ServiceUnavailable, HttpStatusCode.GatewayTimeout,
    HttpStatusCode.HttpVersionNotSupported, HttpStatusCode.VariantAlsoNegotiates, HttpStatusCode.InsufficientStorage,
    HttpStatusCode.LoopDetected, HttpStatusCode.NotExtended, HttpStatusCode.NetworkAuthenticationRequired];

  private clientUnauthorizedAndForbiddenCodes: HttpStatusCode[] = [HttpStatusCode.Unauthorized,
    HttpStatusCode.Forbidden];

  private clientErrorCodes: HttpStatusCode[] = [HttpStatusCode.BadRequest, HttpStatusCode.PaymentRequired,
    HttpStatusCode.NotFound, HttpStatusCode.MethodNotAllowed, HttpStatusCode.NotAcceptable,
    HttpStatusCode.ProxyAuthenticationRequired, HttpStatusCode.RequestTimeout, HttpStatusCode.Conflict,
    HttpStatusCode.Gone, HttpStatusCode.LengthRequired, HttpStatusCode.PreconditionFailed,
    HttpStatusCode.PayloadTooLarge, HttpStatusCode.UriTooLong, HttpStatusCode.UnsupportedMediaType,
    HttpStatusCode.RangeNotSatisfiable, HttpStatusCode.ExpectationFailed, HttpStatusCode.ImATeapot,
    HttpStatusCode.MisdirectedRequest, HttpStatusCode.UnprocessableEntity, HttpStatusCode.Locked,
    HttpStatusCode.FailedDependency, HttpStatusCode.TooEarly, HttpStatusCode.UpgradeRequired,
    HttpStatusCode.PreconditionRequired, HttpStatusCode.TooManyRequests, HttpStatusCode.RequestHeaderFieldsTooLarge,
    HttpStatusCode.UnavailableForLegalReasons];

  private readonly flowableConfigurationUrl: string = this.apiUrlService.sngFlwWorkApiUrl + Constants.SLASH
    + this.flowableApiUrlService.flwFrontendConfigurationApi;

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return next.handle(request).pipe(
      catchError((error: HttpErrorResponse) => {
        this.detectErrors(error);
        return this.throwError(error);
      }),
    );
  }

  /**
   * Handles HTTP error responses and performs appropriate actions based on the error status.
   * @param error The HTTP error response object.
   * @returns The error response object after handling it.
   */
  detectErrors(error: HttpErrorResponse): Observable<never> {
    // Handle client unauthorized and forbidden errors
    if (this.clientUnauthorizedAndForbiddenCodes.includes(error.status)) {
      // Redirect to access-denied page if in iframe or trigger login process
      if (this.iframeService.isIframePage()) {
        this.backgroundTaskService.runInBackground(
          this.router.navigate([Links.ERROR_LINK_URL, Links.ACCESS_DENIED_LINK_URL]),
        );
      } else if (!this.oidcAuthService.isStartedLoginProcess()) {
        this.backgroundTaskService.runInBackground(this.oidcAuthService.login());
        this.oidcAuthService.isStartedLoginProcess.set(true);
      }
      return this.throwError(error);
    }

    // Handle client error codes
    if (this.clientErrorCodes.includes(error.status)) {
      // Check if error URL is related to specific API and redirect or show error accordingly
      if (error.url.includes(this.apiUrlService.sngFlwWorkApiUrl)) {
        return this.throwError(error);
      }
      if (!error.url.includes('documents/download')) {
        this.backgroundTaskService.runInBackground(
          this.router.navigate([Links.ERROR_LINK_URL, Links.BAD_REQUEST_LINK_URL]),
        );
      }
      return this.throwError(error);
    }

    // Handle server error codes except for 'abort' type errors
    if (
      this.serverErrorCodes.includes(error.status)
      && error.error.type !== 'abort'
      && !error.url.includes(this.flowableConfigurationUrl)
    ) {
      // Redirect to time-out error page with current URL as query parameter
      this.backgroundTaskService.runInBackground(this.router.navigate(
        [Links.ERROR_LINK_URL, Links.TIME_OUT_LINK_URL],
        {queryParams: {url: window.location.href}},
      ));
      return this.throwError(error);
    }

    // For any other errors, just return the error response
    return this.throwError(error);
  }

  /**
   * Get the error message from the HttpErrorResponse object.
   * If the error message is not available, return the status text.
   * @param error - The HttpErrorResponse object containing the error information.
   * @returns The error message or status text.
   */
  getError(error: HttpErrorResponse): string {
    return (error?.error?.message) || error.statusText;
  }

  /**
   * Throws an observable error
   * @param error - the HTTP error response
   * @returns an observable that never emits a value but only emits an error
   */
  throwError(error: HttpErrorResponse): Observable<never> {
    if (error.url.includes(this.apiUrlService.sngFlwWorkApiUrl)) {
      return this.throwFlwError(error);
    }
    return throwError(() => this.getError(error));

  }

  /**
   * Throws a Flow error based on the provided HttpErrorResponse.
   * @param errorResponse - The HttpErrorResponse to generate the error from.
   * @returns An Observable that emits the error.
   */
  throwFlwError(errorResponse: HttpErrorResponse): Observable<never> {
    return throwError(() => ({
      error: errorResponse?.error,
      status: errorResponse?.status,
    }));
  }

}
